import React from "react";
import { useSelector } from "react-redux";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./Progress.styles";
import { StoreState } from "../../models/states/StoreState";
import { SurveyMode } from "../../models/enums/SurveyMode";
import { some, find } from "../../utils/lodash";

interface Props {
    value: number;
    position?: "top" | "bottom";
}

function progressTextPosition(theme: any) {
    if (!theme.ProgressBarShowValue) {
        return "none";
    }
    if (theme.ProgressBarHeight >= theme.ProgressBarFontSize) {
        return "inner";
    }
    if (theme.ProgressBarType === "floating") {
        return "floating";
    }
    return "bottom";
}

function progressBarPosition(theme: any) {
    return theme.ProgressBarType;
}

export default function Progress(props: Props) {
    const { value, position } = props;
    const boundedValue = Math.max(Math.min(value, 100), 0);

    const simulated = useSelector(
        (state: StoreState) =>
            process.env.NODE_ENV !== "development" && // Do not show simulated warning if we are in dev.
            state.interview.respondent &&
            state.interview.respondent.simulated,
    );
    const simulatedBannerVisible =
        useSelector((state: StoreState) => state.application.mode !== SurveyMode.Normal) &&
        simulated;

    const progressBarRef = React.useRef(null);
    const [progressBarWidth, setProgressBarWidth] = React.useState(0);
    const [hover, setHover] = React.useState(false);
    const [isMobilePreview, setIsMobilePreview] = React.useState(false);

    const theme = useTheme();
    const classes = useStyles({
        theme,
        position,
        simulatedBannerVisible,
        isMobilePreview,
    });

    const textPosition = progressTextPosition(theme);

    const onWindowSizeChanged = React.useCallback(() => {
        if (!progressBarRef || !progressBarRef.current) {
            return;
        }
        // Adjust progress bar position on mobile preview
        if (progressBarPosition(theme) === "floating") {
            const deviceFrame = progressBarRef.current.closest(".device-frame");
            if (deviceFrame) {
                if (!some(deviceFrame.childNodes, (child) => child === progressBarRef.current)) {
                    // Remove any other progress bar with same position
                    const child = find(
                        deviceFrame.childNodes,
                        (c: HTMLElement) => c.getAttribute("data-position") === position,
                    );
                    if (child) deviceFrame.removeChild(child);
                    // Move progress bar the its new position
                    deviceFrame.appendChild(progressBarRef.current);
                }
            }
        }
        setIsMobilePreview(progressBarRef.current.closest(".preview__mobile") != null);
        setProgressBarWidth(progressBarRef.current.getBoundingClientRect().width);
    }, []);

    React.useLayoutEffect(() => {
        onWindowSizeChanged();
        window.addEventListener("resize", onWindowSizeChanged);
        return function cleanup() {
            window.removeEventListener("resize", onWindowSizeChanged);
        };
    });

    return (
        <aside
            className={classes.progress}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
        >
            <div
                ref={progressBarRef}
                className={classes.bar}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={boundedValue}
                data-position={position}
            >
                {textPosition === "inner" && (
                    <div
                        className={classes.innerValue}
                        style={{ left: progressBarWidth / 2 }}
                    >{`${Math.round(boundedValue)} %`}</div>
                )}
                <div className={classes.fill} style={{ width: `${boundedValue}%` }}>
                    {textPosition === "inner" && (
                        <div
                            className={classes.innerFillValue}
                            style={{ left: progressBarWidth / 2 }}
                        >{`${Math.round(boundedValue)} %`}</div>
                    )}
                </div>
            </div>
            {textPosition === "bottom" && (
                <div className={classes.bottomValue}>{`${Math.round(boundedValue)} %`}</div>
            )}
            {textPosition === "floating" && (
                <div
                    className={classnames(
                        classes.floatingValue,
                        hover && classes.floatingValueHover,
                    )}
                    style={{
                        left: Math.min(
                            progressBarWidth - 50,
                            Math.max(50, (progressBarWidth * boundedValue) / 100),
                        ),
                    }}
                >
                    {`${Math.round(boundedValue)} %`}
                    <div className={classes.floatingValueArrowBorder} />
                    <div className={classes.floatingValueArrow} />
                </div>
            )}
        </aside>
    );
}
